import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AttendanceTodaySummary, DashboardData } from '../models/dashboard.model';
import { NotificationService } from './notification.service';
import { StudentService } from './student.service';
import { UserProfileService } from './user-profile.service';
import { TenantService } from './tenant.service';

/**
 * Dashboard metrics. Student & teacher counts come from list endpoints (length),
 * so you do not need separate /count APIs for those tiles.
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  private readonly base = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private studentService: StudentService,
    private userProfileService: UserProfileService,
    private tenantService: TenantService
  ) { }

  loadDashboard(): Observable<DashboardData> {
    return forkJoin({
      studentCount: this.fetchStudentCountFromList(),
      teacherCount: this.fetchTeacherCountFromList(),
      attendanceToday: this.fetchAttendanceToday(),
      unreadNotifications: this.fetchUnreadCount(),
      tenantDisplayName: this.fetchTenantDisplayName()
    });
  }

  private fetchTenantDisplayName(): Observable<string | null> {
    return this.tenantService.getDisplayName().pipe(
      map(name => (name && name.trim() ? name.trim() : null)),
      catchError(() => of(null))
    );
  }

  private fetchStudentCountFromList(): Observable<number | null> {
    return this.studentService.getAll().pipe(
      map(list => (Array.isArray(list) ? list.length : 0)),
      catchError(() => of(null))
    );
  }

  private fetchTeacherCountFromList(): Observable<number | null> {
    return this.userProfileService.listUsers().pipe(
      map(list => {
        if (!Array.isArray(list)) {
          return 0;
        }
        return list.filter(u => (u.roles || []).includes('ROLE_TEACHER')).length;
      }),
      catchError(() => of(null))
    );
  }

  private fetchAttendanceToday(): Observable<AttendanceTodaySummary | null> {
    return this.http.get<unknown>(`${this.base}/attendance/summary/today`).pipe(
      map(body => this.parseAttendance(body)),
      catchError(() => of(null))
    );
  }

  private fetchUnreadCount(): Observable<number | null> {
    return this.notificationService.getUnreadCount().pipe(
      catchError(() => of(null))
    );
  }

  private parseAttendance(body: unknown): AttendanceTodaySummary | null {
    if (body == null) {
      return null;
    }
    if (typeof body === 'number') {
      return { presentPercent: body };
    }
    if (typeof body !== 'object') {
      return null;
    }
    const o = body as Record<string, unknown>;
    const pct = o.presentPercent ?? o.percentage ?? o.presentPercentage;
    const presentPercent = typeof pct === 'number' ? pct : typeof pct === 'string' ? Number(pct) : undefined;
    return {
      date: typeof o.date === 'string' ? o.date : undefined,
      presentPercent: presentPercent != null && !Number.isNaN(presentPercent) ? presentPercent : undefined,
      recordedClasses: typeof o.recordedClasses === 'number' ? o.recordedClasses : undefined,
      presentCount: typeof o.presentCount === 'number' ? o.presentCount : undefined,
      absentCount: typeof o.absentCount === 'number' ? o.absentCount : undefined
    };
  }
}
